// src/pages/Ligue/CommissionsLigue.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getAllTeamLigue } from "./../../services/teamLigueServices";
import { getAllCommissions } from "./../../services/commissionsServices";
import { useNavigate } from "react-router-dom";
import useScrollToTop from "./../../hooks/useScollToTop";
import { FaSearch, FaUsers, FaSitemap } from "react-icons/fa";
import HeroImage from "../../components/Hero/HeroImage";
import MemberCard from "./components/MemberCard";
import CommissionCard from "./components/CommissionCard";
import CommissionDetailsModal from "./components/CommissionDetailsModal";

/* ========================= Helpers ========================= */
const normalize = (s = "") =>
  s.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const toAnchorId = (label = "") => normalize(label).replace(/[^a-z0-9]+/g, "-");

/* ========================= UI atoms ========================= */
const Section = ({ id, icon: Icon, title, subtitle, children, right }) => (
  <section id={id} className="w-full max-w-[1280px] mx-auto px-4">
    <div className="flex items-end justify-between gap-4 mb-4">
      <div>
        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300 mt-4">
          {Icon ? <Icon className="text-lg" /> : null}
          <span className="uppercase tracking-wide text-xs">Ligue Nouvelle-Aquitaine</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold mt-1">{title}</h2>
        {subtitle ? (
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{subtitle}</p>
        ) : null}
      </div>
      {right}
    </div>
    {children}
  </section>
);

const Toolbar = ({ search, setSearch }) => (
  <div className="bg-white/90 dark:bg-slate-800/80 ring-1 ring-slate-200/70 dark:ring-slate-700/70 rounded-2xl p-4 flex items-stretch gap-3">
    <div className="flex-1 relative">
      <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Rechercher un membre (nom, prénom, fonction, email)…"
        className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70"
      />
    </div>
  </div>
);

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-200 dark:bg-slate-700 rounded-md ${className}`} />
);
const PeopleSkeletonGrid = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="rounded-2xl p-4 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        <div className="flex items-center gap-4">
          <Skeleton className="w-24 h-24 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-4 w-40 mb-2" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ========================= Main Page ========================= */
const CommissionsLigue = () => {
  useScrollToTop();
  const navigate = useNavigate();

  const [membres, setMembres] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Recherche seulement (plus de filtre commission)
  const [search, setSearch] = useState("");

  // Modale Commission
  const [modalCommission, setModalCommission] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [membresData, commissionsData] = await Promise.all([
          getAllTeamLigue(),
          getAllCommissions(),
        ]);
        setMembres(Array.isArray(membresData) ? membresData : []);
        setCommissions(Array.isArray(commissionsData) ? commissionsData : []);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleNavigation = (path) => navigate(path);

  // Pour les liens rapides sous le Hero
  const commissionNames = useMemo(() => {
    const names = new Set();
    membres.forEach((m) => m?.commission?.nom_commission && names.add(m.commission.nom_commission));
    commissions.forEach((c) => c?.nom_commission && names.add(c.nom_commission));
    return Array.from(names).sort((a, b) => a.localeCompare(b, "fr"));
  }, [membres, commissions]);

  // Liste unique des membres, triée par id (asc) + recherche
  const filteredMembers = useMemo(() => {
    let rows = [...membres];

    if (search.trim()) {
      const q = normalize(search);
      rows = rows.filter((m) => {
        const hay = [m.nom, m.prenom, m.poste, m.email].filter(Boolean).map(normalize).join(" ");
        return hay.includes(q);
      });
    }

    // Tri strict par id croissant (président id=1 en tête, etc.)
    rows.sort((a, b) => (Number(a?.id ?? 0) - Number(b?.id ?? 0)));
    return rows;
  }, [membres, search]);

  const membersForCommissionName = (name) =>
    membres.filter((m) => m?.commission?.nom_commission === name);

  return (
    <div className="w-full pt-[70px] font-satoshi">
      {/* HERO */}
      <HeroImage
        title="Équipe & Commissions"
        subtitle="Ligue Nouvelle-Aquitaine • Judo"
        imageUrl="/img/membres_ligue.jpg"
      />

      {/* ===== Membres (tri par id) ===== */}
      <Section
        id="membres"
        icon={FaUsers}
        title="Membres de la Ligue"
        right={<Toolbar search={search} setSearch={setSearch} />}
      >
        {loading ? (
          <PeopleSkeletonGrid />
        ) : filteredMembers.length === 0 ? (
          <div className="rounded-2xl p-6 text-center ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80">
            Aucun membre trouvé.
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMembers.map((p) => (
              <MemberCard key={p.id} p={p} />
            ))}
          </div>
        )}
      </Section>

      {/* ===== Commissions ===== */}
      <Section
        id="commissions"
        icon={FaSitemap}
        title="Les Commissions"
        subtitle="Aperçu des commissions de la Ligue et des membres rattachés."
      >
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 p-4">
                <Skeleton className="h-40 w-full rounded-xl mb-3" />
                <Skeleton className="h-4 w-44 mb-2" />
                <Skeleton className="h-3 w-64" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4 mb-12">
            {commissions.map((c) => (
              <CommissionCard
                key={c.id}
                c={c}
                members={membersForCommissionName(c.nom_commission)}
                onOpen={() => setModalCommission(c)}
              />
            ))}
          </div>
        )}
      </Section>

      {/* Modale Détail Commission */}
      <CommissionDetailsModal
        isOpen={!!modalCommission}
        onClose={() => setModalCommission(null)}
        commission={modalCommission}
        members={modalCommission ? membersForCommissionName(modalCommission.nom_commission) : []}
      />
    </div>
  );
};

export default CommissionsLigue;
